/**
 * Route matching with no document behind it.
 *
 * The browser router and the server render both answer "which route is this
 * URL, and with what params". Kept apart from the runtime, which owns outlets
 * and effects, so a server can import it without a DOM.
 */
import { flag } from '@crown/flags';
import { isFunction, isObjectLike } from '@crown/framework/ir';
import type { ComponentFunction, RouteConfig } from '../types';
import { type RemoteRoute, remoteComponent, serverRemoteComponent } from './remote';

export type RouteParams = Record<string, string | number | boolean>;

/** The attribute on an outlet whose content the server rendered, naming the route path it rendered. */
export const ROUTE_ATTRIBUTE = 'data-c-route';

/** A route path as both sides compare it: no leading or trailing slash, no query. */
export function routeKey(path: string): string {
  return path.replace(/[?#].*$/, '').replace(/^\/+|\/+$/g, '');
}

/** Whether a route is there for the caller: one that names a flag is only while the flag is on. A tracked read. */
export function routeEnabled(route: Pick<RouteConfig, 'flag'>): boolean {
  return !route.flag || flag(route.flag);
}

/** Matches a path against a route pattern (`/cases/:id`); the params it bound, or null. */
export function matchPath(pattern: string, path: string): { params: Record<string, string> } | null {
  const normalizedPattern = pattern.replace(/^\/+|\/+$/g, '');
  // A query string or fragment is not part of the route: `organizations?action=create`
  // is the organizations route with a query on it, not an unknown page.
  const normalizedPath = path.replace(/[?#].*$/, '').replace(/^\/+|\/+$/g, '');
  const p = normalizedPattern ? normalizedPattern.split('/') : [];
  const s = normalizedPath ? normalizedPath.split('/') : [];
  if (p.length !== s.length) return null;
  const params: Record<string, string> = {};
  for (const [i, ps] of p.entries()) {
    const ss = s[i];
    if (ss === undefined) return null;
    if (ps.startsWith(':')) {
      const name = ps.slice(1).replace(/\?$/, '');
      params[name] = decodeURIComponent(ss);
    } else if (ps !== ss) return null;
  }
  return { params };
}

export function joinPaths(a: string, b: string): string {
  if (!a || a === '/') return b;
  if (!b) return a;
  return `${a.replace(/\/$/, '')}/${b.replace(/^\//, '')}`;
}

/** An object or a function, which is what can carry a `then` or a `default`; null for anything else. */
function propertyHolder<T>(value: T): object | null {
  return isObjectLike(value) || isFunction(value) ? value : null;
}

/** Whether a loader handed back a promise, to await, rather than the value itself. */
export function isPromiseLike<T>(value: T | PromiseLike<T>): value is PromiseLike<T> {
  const holder = propertyHolder(value);
  return holder !== null && 'then' in holder && isFunction(holder.then);
}

/** Whether what a loader produced is a module whose default export is the thing wanted. */
function hasDefault<T>(loaded: T | { readonly default: T }): loaded is { readonly default: T } {
  const holder = propertyHolder(loaded);
  return holder !== null && 'default' in holder && holder.default !== null && holder.default !== undefined;
}

/** A loaded module's default export, or what was loaded when it is the thing itself. */
export function moduleDefault<T>(loaded: T | { readonly default: T }): T {
  return hasDefault(loaded) ? loaded.default : loaded;
}

/** A route's component, loading it first when the route names it lazily. */
export async function resolveComponent<T>(route: RouteConfig<T>): Promise<ComponentFunction<T> | null> {
  if (route.remote) {
    // SAFETY: `remote` was checked just above, and a remote's page takes whatever data its route loads.
    return remoteComponent(route as RemoteRoute, false) as ComponentFunction<T>;
  }
  if (route.component) {
    // SAFETY: a lazy `component` is handed back as it is, as it always has been, and the outlet calls it like any other.
    return route.component as ComponentFunction<T>;
  }
  if (route.loadComponent) {
    const res = route.loadComponent();
    const awaited = isPromiseLike(res) ? await res : res;
    // SAFETY: the component a route loads lazily is that route's own, so it takes the data the route loads.
    return moduleDefault(awaited) as ComponentFunction<T>;
  }
  return null;
}

/** Every route in a table with its full path, children after their parent, as the router indexes them. */
export function flattenRouteTable(routes: readonly RouteConfig[], parent = ''): RouteConfig[] {
  const out: RouteConfig[] = [];
  for (const r of routes) {
    const full = joinPaths(parent, r.path);
    out.push({ ...r, path: full });
    if (r.children) out.push(...flattenRouteTable(r.children, full));
  }
  return out;
}

/**
 * Finds the route for `path`, expanding a lazy parent (`loadChildren`) whose
 * prefix covers it, as the browser router does on a deep link.
 */
export async function findRoute(routes: RouteConfig[], path: string): Promise<RouteConfig | null> {
  const direct = routes.find((r) => routeEnabled(r) && matchPath(r.path, path));
  if (direct) return direct;
  const segments = path.split('/').filter(Boolean);
  for (let i = segments.length; i > 0; i--) {
    const prefix = segments.slice(0, i).join('/');
    const parent = routes.find((r) => r.loadChildren && matchPath(r.path, prefix));
    if (!parent?.loadChildren) continue;
    const mod = await parent.loadChildren();
    if (mod?.routes) {
      for (const r of flattenRouteTable(mod.routes, parent.path)) {
        if (!routes.some((known) => known.path === r.path)) routes.push(r);
      }
    }
    const found = routes.find((r) => routeEnabled(r) && matchPath(r.path, path));
    if (found) return found;
  }
  return null;
}

/** A route's component for a server render: a remote route writes its unavailable card, and the browser loads the remote. */
export async function resolveServerComponent<T>(route: RouteConfig<T>, params: RouteParams = {}): Promise<ComponentFunction<T> | null> {
  if (route.remote) {
    // SAFETY: `remote` was checked just above, and a remote's page takes whatever data its route loads.
    return (await serverRemoteComponent(route as RemoteRoute, params)) as ComponentFunction<T>;
  }
  return resolveComponent(route);
}
